import { useMemo, useState } from "react";
import {
  ASSUMPTIONS,
  DEFAULT_REPAT,
  REPAT_LIMITS as L,
  compareTimings,
  runRepatriation,
  sanitizeRepat,
} from "../model/repatriation.ts";
import type { IndiaStatus, LandingAccount, RepatriationInputs } from "../model/repatriation.ts";
import { crore, money, pct, rupees, usd } from "../lib/format.ts";
import { NumberField } from "./NumberField.tsx";
import { isExampleRepat } from "../lib/repatStore.ts";
import { Waterfall } from "./Waterfall.tsx";
import type { WaterfallStep } from "./Waterfall.tsx";

const toPct = (v: number) => Math.round(v * 10000) / 100;
const fromPct = (v: number) => v / 100;

const STATUS: { id: IndiaStatus; label: string; detail: string }[] = [
  { id: "nri", label: "NRI", detail: "Still living outside India. India taxes only Indian income." },
  { id: "rnor", label: "RNOR", detail: "Resident but not ordinarily resident — usually the first 2–3 years back. Foreign income is not taxed in India." },
  { id: "ror", label: "ROR", detail: "Resident and ordinarily resident. India taxes worldwide income, with credit for US federal tax." },
];

const ACCOUNTS: { id: LandingAccount; label: string; detail: string }[] = [
  { id: "nre", label: "NRE", detail: "Rupees. For non-residents. Interest tax-free in India; fully repatriable. Redesignate once resident." },
  { id: "nro", label: "NRO", detail: "Rupees. For non-residents' Indian income. Interest taxable, with TDS." },
  { id: "fcnr", label: "FCNR(B)", detail: "US-dollar deposit. Opened only while a non-resident; can run to maturity after you return." },
  { id: "rfc", label: "RFC", detail: "US-dollar account for returning residents. Keeps the money in dollars until you choose to convert." },
  { id: "resident", label: "Resident account", detail: "Ordinary rupee savings or deposits once you live in India. Interest taxed at your slab." },
];

export function RepatriationPlanner({
  inputs,
  onChange: setRaw,
}: {
  inputs: RepatriationInputs;
  onChange: (next: RepatriationInputs) => void;
}) {
  const [notices, setNotices] = useState<string[]>([]);
  const set = (patch: Partial<RepatriationInputs>) => {
    const checked = sanitizeRepat({ ...inputs, ...patch });
    setRaw(checked.inputs);
    setNotices(checked.notices);
  };
  const setRe = (patch: Partial<RepatriationInputs["realEstate"]>) =>
    set({ realEstate: { ...inputs.realEstate, ...patch } });

  const r = useMemo(() => runRepatriation(inputs), [inputs]);
  const timings = useMemo(() => compareTimings(inputs), [inputs]);
  const best = timings.reduce((a, b) => (b.result.netInr > a.result.netInr ? b : a));
  const growthYears = inputs.compound ? Math.max(0, inputs.saleYear - inputs.balancesAsOfYear) : 0;
  const fx = inputs.fxRate;

  const steps: WaterfallStep[] = [
    { label: "Gross\nassets", value: r.grossUsd * fx, kind: "total", tone: "total" },
    { label: "Forfeited\n& costs", value: r.lostUsd * fx, kind: "deduction", tone: "lost" },
    { label: "US tax", value: r.usTaxUsd * fx, kind: "deduction", tone: "ustax" },
    { label: "US\npenalties", value: r.penaltyUsd * fx, kind: "deduction", tone: "penalty" },
    { label: "FX spread\n& wire", value: r.feesUsd * fx, kind: "deduction", tone: "fees" },
    { label: "Indian tax", value: r.indiaTaxInr, kind: "deduction", tone: "india" },
    { label: "Net in\nIndia", value: r.netInr, kind: "total", tone: "total" },
  ];

  const isExample = isExampleRepat(inputs);

  return (
    <div className="repat">
      <header className="page-head">
        <p className="eyebrow">Moving money home</p>
        <h2>USD to INR Savings &amp; Repatriation Planner</h2>
        <p className="dek">
          What actually lands in India if you liquidate your US assets — after US tax and penalties,
          forfeited match and selling costs, the FX spread and wire fees, and Indian tax. Change when
          you sell, and watch the number move.
        </p>
      </header>

      <div className="layout">
        <aside>
          <form className="inputs" onSubmit={(e) => e.preventDefault()}>
            <p className="input-tip">
              Amounts accept shorthand: <kbd>250k</kbd>, <kbd>1.2M</kbd> or <kbd>820,000</kbd>.
            </p>
            {isExample && (
              <p className="example-banner">
                These are example figures. Replace them with your own — they stay in this browser.
              </p>
            )}
            {notices.length > 0 && (
              <div className="notices" role="status">
                <strong>Adjusted to stay consistent</strong>
                <ul>{notices.map((n) => <li key={n}>{n}</li>)}</ul>
              </div>
            )}

            <fieldset>
              <legend>Cash and brokerage — as they stand today (USD)</legend>
              <NumberField label="Checking & savings" unit="usd" value={inputs.cash} limit={L.cash} onCommit={(v) => set({ cash: v })} />
              <NumberField label="Taxable brokerage — total value" unit="usd" value={inputs.brokerageValue} limit={L.brokerageValue} onCommit={(v) => set({ brokerageValue: v })} hint="Include vested RSU and ESPP shares you still hold." />
              <NumberField indent label="Unrealised short-term gain" unit="usd" value={inputs.brokerageShortGain} limit={L.gain} onCommit={(v) => set({ brokerageShortGain: v })} hint="Held a year or less." />
              <NumberField indent label="Unrealised long-term gain" unit="usd" value={inputs.brokerageLongGain} limit={L.gain} onCommit={(v) => set({ brokerageLongGain: v })} hint="Held more than a year." />
              <NumberField indent label="Average ₹ per $ when you bought (optional)" value={inputs.brokerageBuyFx} limit={L.buyFx} onCommit={(v) => set({ brokerageBuyFx: v })} hint="India computes gains in rupees, so a weaker rupee since purchase adds to the taxable gain. 0 = ignore." />
            </fieldset>

            <fieldset>
              <legend>Retirement accounts — today (USD)</legend>
              <NumberField label="401(k) balance" unit="usd" value={inputs.k401} limit={L.retirement} onCommit={(v) => set({ k401: v })} />
              <NumberField indent label="Unvested employer match in that balance" unit="usd" value={inputs.k401UnvestedMatch} limit={L.retirement} onCommit={(v) => set({ k401UnvestedMatch: v })} hint="Forfeited when you leave the employer. Check your plan's vesting schedule." />
              <div className="field checkbox indent">
                <input id="r55" type="checkbox" checked={inputs.ruleOf55} onChange={(e) => set({ ruleOf55: e.target.checked })} />
                <label htmlFor="r55">I left this employer in or after the year I turned 55</label>
                <small className="hint">The rule of 55: no 10% penalty on that employer's 401(k). Does not apply to IRAs.</small>
              </div>
              <NumberField label="Traditional IRA balance" unit="usd" value={inputs.tradIra} limit={L.retirement} onCommit={(v) => set({ tradIra: v })} hint="Include rollover IRAs." />
              <NumberField label="Roth — contributions" unit="usd" value={inputs.rothContributions} limit={L.retirement} onCommit={(v) => set({ rothContributions: v })} hint="What you put in. Always comes out tax- and penalty-free." />
              <NumberField indent label="Roth — earnings" unit="usd" value={inputs.rothEarnings} limit={L.retirement} onCommit={(v) => set({ rothEarnings: v })} />
              <div className="field checkbox indent">
                <input id="r5y" type="checkbox" checked={inputs.rothFiveYears} onChange={(e) => set({ rothFiveYears: e.target.checked })} />
                <label htmlFor="r5y">First Roth contribution was five or more tax years ago</label>
              </div>
            </fieldset>

            <fieldset>
              <legend>Other assets (optional)</legend>
              <NumberField label="HSA balance" unit="usd" value={inputs.hsa} limit={L.hsa} onCommit={(v) => set({ hsa: v })} />
              <NumberField indent label="Saved receipts for past medical costs" unit="usd" value={inputs.hsaReceipts} limit={L.hsa} onCommit={(v) => set({ hsaReceipts: v })} hint="Reimbursable from the HSA tax-free, even years later." />
              <NumberField label="RSUs vesting before you sell" unit="usd" value={inputs.rsuVesting} limit={L.rsu} onCommit={(v) => set({ rsuVesting: v })} hint="Taxed as wages at vest. ISOs involve AMT and are not modelled." />

              <div className="field checkbox">
                <input id="re" type="checkbox" checked={inputs.realEstate.include} onChange={(e) => setRe({ include: e.target.checked })} />
                <label htmlFor="re">Include US real estate</label>
              </div>
              {inputs.realEstate.include && (
                <>
                  <NumberField indent label="Expected sale price" unit="usd" value={inputs.realEstate.value} limit={L.property} onCommit={(v) => setRe({ value: v })} />
                  <NumberField indent label="Mortgage to pay off" unit="usd" value={inputs.realEstate.mortgage} limit={L.property} onCommit={(v) => setRe({ mortgage: v })} />
                  <NumberField indent label="Purchase price plus improvements" unit="usd" value={inputs.realEstate.basis} limit={L.property} onCommit={(v) => setRe({ basis: v })} />
                  <NumberField indent label="Selling costs" value={inputs.realEstate.sellingCostPct} limit={L.sellingCost} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => setRe({ sellingCostPct: v })} hint="Agent, closing and transfer costs. 5–7% is typical." />
                  <div className="field checkbox indent">
                    <input id="rep" type="checkbox" checked={inputs.realEstate.primary} onChange={(e) => setRe({ primary: e.target.checked })} />
                    <label htmlFor="rep">Primary home — lived in 2 of the last 5 years</label>
                    <small className="hint">Excludes $250,000 of gain ($500,000 jointly). After moving, you have about three years to sell and keep it.</small>
                  </div>
                  {!inputs.realEstate.primary && (
                    <NumberField indent label="Depreciation claimed as a rental" unit="usd" value={inputs.realEstate.depreciation} limit={L.property} onCommit={(v) => setRe({ depreciation: v })} hint="Recaptured at up to 25%." />
                  )}
                </>
              )}
            </fieldset>

            <fieldset>
              <legend>When you sell, and your status</legend>
              <div className="field">
                <span className="field-label" id="timing">When you sell</span>
                <div className="segmented two" role="radiogroup" aria-labelledby="timing">
                  {(["before-move", "after-move"] as const).map((t) => (
                    <button key={t} type="button" role="radio" aria-checked={inputs.timing === t}
                      className={inputs.timing === t ? "on" : undefined}
                      onClick={() => set({ timing: t, indiaStatus: t === "before-move" ? "nri" : inputs.indiaStatus === "nri" ? "rnor" : inputs.indiaStatus })}>
                      {t === "before-move" ? "Before I move" : "After I move"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field checkbox">
                <input id="usp" type="checkbox" checked={inputs.usPerson} onChange={(e) => set({ usPerson: e.target.checked })} />
                <label htmlFor="usp">I am a US citizen or green-card holder</label>
                <small className="hint">
                  {inputs.usPerson
                    ? "You stay a US taxpayer after moving. Surrendering a green card has its own rules, not modelled here."
                    : "After moving you become a nonresident alien: the US stops taxing your share sales."}
                </small>
              </div>

              <div className="field">
                <span className="field-label" id="india">Indian residential status at the time</span>
                <div className="segmented" role="radiogroup" aria-labelledby="india">
                  {STATUS.map((s) => (
                    <button key={s.id} type="button" role="radio" aria-checked={inputs.indiaStatus === s.id}
                      className={inputs.indiaStatus === s.id ? "on" : undefined}
                      onClick={() => set({ indiaStatus: s.id })}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <small className="hint">{STATUS.find((s) => s.id === inputs.indiaStatus)?.detail}</small>
              </div>

              <NumberField label="Your age when you withdraw" value={inputs.ageAtSale} limit={L.age} onCommit={(v) => set({ ageAtSale: v })} hint={inputs.ageAtSale < 59.5 ? "Under 59½: the 10% early-withdrawal penalty applies." : "59½ or over: no early-withdrawal penalty."} />
              <NumberField label="Year you withdraw" value={inputs.saleYear} limit={L.saleYear} onCommit={(v) => set({ saleYear: v })} hint={growthYears > 0 ? `${growthYears} year${growthYears === 1 ? "" : "s"} of growth is applied to the balances above. Tax rates are 2026 and are not projected forward.` : "Tax rates are 2026 and are not projected forward."} />

              <div className="field checkbox">
                <input id="cmp" type="checkbox" checked={inputs.compound} onChange={(e) => set({ compound: e.target.checked })} />
                <label htmlFor="cmp">Grow the balances to that year</label>
                <small className="hint">
                  Off: the figures above are treated as what you will have at the sale.
                </small>
              </div>
              {inputs.compound && (
                <>
                  <NumberField indent label="Balances above are as of" value={inputs.balancesAsOfYear} limit={L.saleYear} onCommit={(v) => set({ balancesAsOfYear: v })} />
                  <NumberField indent label="Return on investments until then" value={inputs.growthRate} limit={L.growthRate} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ growthRate: v })} hint="Applied to the brokerage, 401(k), IRA, Roth and HSA." />
                  <NumberField indent label="Return on cash" value={inputs.cashRate} limit={L.growthRate} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ cashRate: v })} />
                  <NumberField indent label="Property appreciation" value={inputs.propertyGrowthRate} limit={L.growthRate} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ propertyGrowthRate: v })} />
                </>
              )}

              <div className="field">
                <label htmlFor="filing">US filing status</label>
                <select id="filing" value={inputs.filing} onChange={(e) => set({ filing: e.target.value as RepatriationInputs["filing"] })}
                  disabled={inputs.timing === "after-move" && !inputs.usPerson}>
                  <option value="mfj">Married filing jointly</option>
                  <option value="single">Single</option>
                </select>
              </div>

              <NumberField label="Other US income that year (USD)" unit="usd" value={inputs.otherUsIncome} limit={L.otherIncome} onCommit={(v) => set({ otherUsIncome: v })} hint="Salary before you leave. It fills the lower brackets first." />

              <div className="field">
                <label htmlFor="method">Federal tax method</label>
                <select id="method" value={inputs.taxMethod} onChange={(e) => set({ taxMethod: e.target.value as RepatriationInputs["taxMethod"] })}>
                  <option value="brackets">2026 brackets (recommended)</option>
                  <option value="marginal">One marginal rate</option>
                </select>
                <small className="hint">A large withdrawal climbs through several brackets. One rate understates it.</small>
              </div>
              {inputs.taxMethod === "marginal" && (
                <NumberField indent label="Federal marginal rate" value={inputs.federalMarginal} limit={L.rate} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ federalMarginal: v })} />
              )}
              <NumberField label="State income tax rate" value={inputs.stateRate} limit={L.stateRate} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ stateRate: v })} hint="North Carolina is a flat 3.99% in 2026. Texas, Florida and Washington have no income tax." />
              {inputs.indiaStatus === "ror" && (
                <NumberField label="Other Indian income that year (₹)" unit="inr" value={inputs.otherIndiaIncome} limit={L.otherIndiaIncome} onCommit={(v) => set({ otherIndiaIncome: v })} />
              )}
            </fieldset>

            <fieldset>
              <legend>The transfer</legend>
              <NumberField label="USD/INR rate" value={inputs.fxRate} limit={L.fxRate} suffix="₹ per $" onCommit={(v) => set({ fxRate: v })} hint="95.91 on 21 Sept 2026. Update it before you rely on the answer." />
              <NumberField label="FX spread / bank markup" value={inputs.fxSpreadPct} limit={L.fxSpread} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ fxSpreadPct: v })} hint="0.3–0.8% on large transfers through a good provider; 2–3% at a retail bank counter." />
              <NumberField label="Wire fees, total (USD)" unit="usd" value={inputs.wireFeesUsd} limit={L.wire} onCommit={(v) => set({ wireFeesUsd: v })} />
              <div className="field">
                <label htmlFor="landing">Where it lands in India</label>
                <select id="landing" value={inputs.landing} onChange={(e) => set({ landing: e.target.value as LandingAccount })}>
                  {ACCOUNTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                <small className="hint">{ACCOUNTS.find((a) => a.id === inputs.landing)?.detail}</small>
              </div>
              <NumberField indent label="Interest rate on that account" value={inputs.landingRate} limit={L.landingRate} suffix="%" toDisplay={toPct} fromDisplay={fromPct} onCommit={(v) => set({ landingRate: v })} hint="For the year-one estimate only." />
            </fieldset>

            <button type="button" className="link" onClick={() => { setRaw(DEFAULT_REPAT); setNotices([]); }}>
              Clear my figures and restore the example
            </button>
          </form>
        </aside>

        <div className="output">
          <div className="tiles three">
            <div className="tile">
              <div className="label">Gross US savings</div>
              <div className="value">{usd(r.grossUsd)}</div>
              <div className="sub">
                {crore(r.grossUsd * fx, 2)} at ₹{fx.toFixed(2)}.
                {r.growth.years > 0
                  ? ` Includes ${usd(r.growth.appliedUsd)} of growth over ${r.growth.years} year${r.growth.years === 1 ? "" : "s"} to ${inputs.saleYear}.`
                  : " As entered — no growth applied."}
              </div>
            </div>
            <div className="tile warn-tile">
              <div className="label">US taxes &amp; penalties</div>
              <div className="value">{usd(r.usTaxUsd + r.penaltyUsd)}</div>
              <div className="sub">
                {usd(r.usTaxUsd)} tax, {usd(r.penaltyUsd)} penalties
                {r.lostUsd > 0 ? `, plus ${usd(r.lostUsd)} forfeited or spent selling` : ""}.
              </div>
            </div>
            <div className="tile primary">
              <div className="label">Net in hand in India</div>
              <div className="value">{crore(r.netInr, 2)}</div>
              <div className="sub">
                {rupees(r.netInr)} after {pct(r.leakage, 1)} total leakage
                {r.indiaTaxInr > 0 ? `, including ${money(r.indiaTaxInr)} Indian tax` : ""}.
              </div>
            </div>
          </div>

          <h3 className="section-title">From gross assets to rupees in India</h3>
          <Waterfall steps={steps} />

          <h3 className="section-title">The same assets, sold at three different moments</h3>
          <div className="table-wrap">
            <table className="timings">
              <thead>
                <tr><th scope="col">When</th><th scope="col">US tax &amp; penalties</th><th scope="col">Indian tax</th><th scope="col">Net in India</th><th scope="col">vs best</th></tr>
              </thead>
              <tbody>
                {timings.map((t) => (
                  <tr key={t.id} className={t.id === best.id ? "highlight" : undefined}>
                    <th scope="row">{t.label}<small>{t.detail}</small></th>
                    <td data-label="US tax & penalties">{usd(t.result.usTaxUsd + t.result.penaltyUsd)}</td>
                    <td data-label="Indian tax">{money(t.result.indiaTaxInr)}</td>
                    <td data-label="Net in India" className="key">{crore(t.result.netInr, 2)}</td>
                    <td data-label="vs best">{t.id === best.id ? "best" : `−${crore(best.result.netInr - t.result.netInr, 2)}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="note">
            {inputs.usPerson
              ? "As a US citizen or green-card holder you are taxed by the US whenever you sell, so the gap between these rows comes mostly from state tax and India's status."
              : "For a visa holder the RNOR window is usually the cheapest time to sell shares: the US has stopped taxing the gains and India has not started. Retirement accounts are different — the US still taxes those after you leave."}
          </p>

          <h3 className="section-title">Asset by asset (USD)</h3>
          <div className="table-wrap">
            <table className="assets">
              <thead>
                <tr><th scope="col">Asset</th><th scope="col">Gross</th><th scope="col">Forfeited / costs</th><th scope="col">US tax</th><th scope="col">Penalty</th><th scope="col">Net</th></tr>
              </thead>
              <tbody>
                {r.lines.map((l) => (
                  <tr key={l.key}>
                    <th scope="row">{l.label}<small>{l.note}</small></th>
                    <td data-label="Gross">{usd(l.gross)}</td>
                    <td data-label="Forfeited / costs">{l.lost ? usd(-l.lost) : "—"}</td>
                    <td data-label="US tax">{l.usTax ? usd(-l.usTax) : "—"}</td>
                    <td data-label="Penalty">{l.penalty ? usd(-l.penalty) : "—"}</td>
                    <td data-label="Net" className="key">{usd(l.netUsd)}</td>
                  </tr>
                ))}
                <tr className="subtotal">
                  <th scope="row">Total</th>
                  <td data-label="Gross">{usd(r.grossUsd)}</td>
                  <td data-label="Forfeited / costs">{usd(-r.lostUsd)}</td>
                  <td data-label="US tax">{usd(-r.usTaxUsd)}</td>
                  <td data-label="Penalty">{usd(-r.penaltyUsd)}</td>
                  <td data-label="Net" className="key">{usd(r.netUsd)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="facts">
            <div>
              <dt>Federal tax on ordinary income</dt>
              <dd>{usd(r.us.federalOrdinary)}{r.us.effectiveOnOrdinary > 0 ? ` (${pct(r.us.effectiveOnOrdinary, 1)} effective)` : ""}</dd>
            </div>
            <div><dt>Federal long-term gains tax</dt><dd>{usd(r.us.federalLtcg)}</dd></div>
            <div><dt>Net investment income tax</dt><dd>{usd(r.us.niit)}</dd></div>
            <div><dt>State tax</dt><dd>{usd(r.us.state)}</dd></div>
            <div><dt>FX spread &amp; wire fees</dt><dd>{usd(r.feesUsd)}</dd></div>
            {inputs.indiaStatus === "ror" && (
              <>
                <div><dt>Indian tax before credit</dt><dd>{money(r.indiaTaxInr + r.indiaCreditInr)}</dd></div>
                <div><dt>Credit for US federal tax</dt><dd>−{money(r.indiaCreditInr)}</dd></div>
              </>
            )}
          </div>

          <div className="landing-card">
            <h4>Landing in {ACCOUNTS.find((a) => a.id === inputs.landing)?.label}</h4>
            {r.landing.warning && <p className="warn">{r.landing.warning}</p>}
            <p>
              Year-one interest at {pct(inputs.landingRate, 2)}: about {money(r.landing.yearOneInterestInr)}
              {r.landing.interestTaxInr > 0
                ? `, of which roughly ${money(r.landing.interestTaxInr)} goes in Indian tax.`
                : ", not taxed in India at your current status."}
            </p>
          </div>

          {r.notes.length > 0 && <ul className="plain-notes">{r.notes.map((n) => <li key={n}>{n}</li>)}</ul>}

          <details className="assumptions">
            <summary>What this page assumes</summary>
            <ul>{ASSUMPTIONS.map((a) => <li key={a}>{a}</li>)}</ul>
          </details>

          <aside className="disclaimer" aria-label="Disclaimer">
            <strong>For estimation only — not tax, legal or financial advice.</strong>
            <p>
              This planner produces estimates from simplified versions of US federal, US state and
              Indian tax rules, using the inputs you provide and 2026 rates. Actual liability depends on
              facts it does not see: your full tax return, treaty positions, the exact dates of your
              move, how residency is determined for each year, state-specific rules, and changes in law.
              It does not calculate withholding, estimated-tax obligations, or reporting duties such as
              FBAR, Form 8938 or Schedule FA. Nothing here is a recommendation to buy, sell or withdraw
              anything. Before acting — particularly on retirement-account withdrawals, the timing of a
              move, or residency status — consult a qualified tax professional licensed in both the
              United States and India.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
